import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@shared/components/ui/tabs";
import { Separator } from "@shared/components/ui/separator";
import { Badge } from "@shared/components/ui/badge";
import { Button } from "@shared/components/ui/button";
import {
  Settings2,
  Package,
  DollarSign,
  Globe,
  TrendingUp,
  Users,
  Activity,
  ExternalLink,
} from "lucide-react";
import { Link } from "react-router-dom";
import SettingsForm from "./SettingsForm";
import { useSettingsQuery } from "./useSettings";
import { useSettings } from '@shared/contexts/SettingContext';
import { useTranslation } from "react-i18next";
import ThreeDotLoader from '@shared/components/ThreeDotLoader';
import { CustomPricingManagement } from "./custom-pricing/CustomPricingManagement";

const Settings = () => {
  const { t } = useTranslation();
  const { data, isLoading } = useSettingsQuery();
  const { numbers } = useSettings();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <ThreeDotLoader />
      </div>
    );
  }

  const statsCards = [
    {
      title: t("stats.totalPackages", { default: "Total Packages" }),
      value: numbers?.packages || 0,
      icon: Package,
      description: t("stats.packagesDescription", {
        default: "Active packages in system",
      }),
      color: "bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400",
    },
    {
      title: t("stats.totalUsers", { default: "Total Users" }),
      value: numbers?.users || 0,
      icon: Users,
      description: t("stats.usersDescription", { default: "Registered users" }),
      color: "bg-green-50 text-green-600 dark:bg-green-950 dark:text-green-400",
    },
    {
      title: t("stats.totalPickups", { default: "Total Pickups" }),
      value: numbers?.pickups || 0,
      icon: Activity,
      description: t("stats.pickupsDescription", {
        default: "Pickup requests",
      }),
      color:
        "bg-orange-50 text-orange-600 dark:bg-orange-950 dark:text-orange-400",
    },
  ];

  return (
    <div className="space-y-6 p-6">

      {/* Settings Tabs */}
      <Tabs defaultValue="general" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="general" className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            {t("settings.general", { default: "General" })}
          </TabsTrigger>
          <TabsTrigger value="pricing" className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            {t("settings.pricing", { default: "Pricing" })}
          </TabsTrigger>
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                {t("settings.generalSettings", { default: "General Settings" })}
              </CardTitle>
              <CardDescription>
                {t("settings.generalDescription", {
                  default: "Configure basic application settings",
                })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SettingsForm settingsData={data} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pricing Settings */}
        <TabsContent value="pricing" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                {t("settings.pricingConfiguration", {
                  default: "Pricing Configuration",
                })}
              </CardTitle>
              <CardDescription>
                {t("settings.pricingDescription", {
                  default: "Configure pricing rules and fees",
                })}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Custom Pricing Management */}
       <CustomPricingManagement />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
